import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { insertUserSchema, User as SelectUser } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { migrateRecentPhotosFromStorage } from "@/lib/recent-photos-storage";
import { useToast } from "@/hooks/use-toast";
import { trackRegistration, track } from "@/lib/analytics";
import { EVENTS } from "@/lib/analytics-events";
import { z } from "zod";

// Extended schema with login validation and privacy/newsletter fields
const registerSchema = insertUserSchema.extend({
  confirmPassword: z.string(),
  acceptedPrivacyPolicy: z.boolean(),
  acceptedNewsletter: z.boolean().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type AuthContextType = {
  user: SelectUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<SelectUser, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<SelectUser, Error, RegisterData>;
  refetch: () => void;
};

export type LoginData = {
  email: string;
  password: string;
};

export type RegisterData = z.infer<typeof registerSchema>;

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();

  // Rinvia il fetch di /api/user al post-hydration SOLO sulle pagine SSR (essay): se la query
  // si risolve durante l'hydration di un boundary SSR, l'update lo fa ricadere su client-render
  // (React #418/#419). Le pagine SSR iniettano window.__SSR_LOCALE__; sulle route CSR pure
  // partiamo già "hydrated" → /api/user parte subito, nessun ritardo dello stato auth (header).
  const wasSsrPage = typeof window !== "undefined" && Boolean((window as any).__SSR_LOCALE__);
  const [hydrated, setHydrated] = useState(!wasSsrPage);
  useEffect(() => setHydrated(true), []);

  const {
    data: user,
    error,
    isLoading,
    refetch: refetchUser,
  } = useQuery<SelectUser | undefined, Error>({
    queryKey: ["/api/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: hydrated,
    refetchOnWindowFocus: false, // Disabilita anche il refetch al focus per evitare chiamate non necessarie
    refetchInterval: false, // Disabilita il refetch automatico per evitare chiamate ripetute che interferiscono con GTM
    refetchOnReconnect: false, // Disabilita il refetch alla riconnessione
    staleTime: 10 * 60 * 1000, // Considera i dati validi per 10 minuti (aumentato da 5)
    gcTime: 30 * 60 * 1000, // Mantiene i dati in cache per 30 minuti (aumentato da 10)
    retry: false, // Disabilita completamente i retry per evitare chiamate multiple
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      console.log("Login attempt with:", credentials.email);
      const res = await apiRequest("POST", "/api/login", credentials);
      const userData = await res.json();
      console.log("Login response:", userData);
      return userData;
    },
    onSuccess: (user: SelectUser) => {
      console.log("Login successful, updating user data:", user);
      // Imposta prima i dati nel cache, poi invalida la query per forzare un refresh
      queryClient.setQueryData(["/api/user"], user);
      // Usa refetchQueries invece di invalidateQueries per forzare un refresh immediato
      queryClient.refetchQueries({ queryKey: ["/api/user"] });
      migrateRecentPhotosFromStorage().catch((e) => console.warn("Recent photos migration failed:", e));
      toast({
        title: "Login successful",
        description: `Welcome back, ${user.displayName || user.email}!`,
      });
    },
    onError: (error: Error) => {
      console.error("Login error:", error);
      track(EVENTS.AUTH_ERROR, { method: 'email', event_category: 'login', event_label: String(error?.message || '').slice(0, 100) });
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (data: RegisterData) => {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...credentials } = data;
      const res = await apiRequest("POST", "/api/register", credentials);
      return await res.json();
    },
    onSuccess: (user: SelectUser) => {
      // Imposta prima i dati nel cache, poi refetch immediato come per il login
      queryClient.setQueryData(["/api/user"], user);
      // Usa refetchQueries invece di invalidateQueries per forzare un refresh immediato
      queryClient.refetchQueries({ queryKey: ["/api/user"] });
      migrateRecentPhotosFromStorage().catch((e) => console.warn("Recent photos migration failed:", e));
      // Traccia l'evento di registrazione email
      trackRegistration('email', user.id, user.email, true);
      toast({
        title: "Registration successful",
        description: "Welcome to Trieste Storica!",
      });
    },
    onError: (error: Error) => {
      track(EVENTS.AUTH_ERROR, { method: 'email', event_category: 'register', event_label: String(error?.message || '').slice(0, 100) });
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/logout");
    },
    onSuccess: () => {
      // Imposta l'utente a null nella cache senza fare nuove chiamate API
      queryClient.setQueryData(["/api/user"], null);
      
      // Invalida solo le query necessarie senza refetch automatico
      queryClient.invalidateQueries({ 
        queryKey: ["/api/user"],
        refetchType: 'none' // Non fare refetch automatico
      });
      
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
      
      // Reindirizza alla home page dopo il logout
      setTimeout(() => {
        window.location.href = "/";
      }, 300);
    },
    onError: (error: Error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Effetto per debug dell'autenticazione
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log("Auth state changed:", { 
        isAuthenticated: !!user,
        email: user?.email,
        isAdmin: user?.isAdmin
      });
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isAuthenticated: !!user,
        // Finché non idratato la query è disabilitata: tieni isLoading truthy così le
        // route protette mostrano lo spinner invece di redirigere prematuramente.
        isLoading: isLoading || !hydrated,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
        refetch: refetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    // In development, log a warning but return a safe default
    if (process.env.NODE_ENV === 'development') {
      console.warn("useAuth called outside AuthProvider, returning default values");
    }
    // Return safe default values instead of throwing
    return {
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      loginMutation: {} as UseMutationResult<SelectUser, Error, LoginData>,
      logoutMutation: {} as UseMutationResult<void, Error, void>,
      registerMutation: {} as UseMutationResult<SelectUser, Error, RegisterData>,
      refetch: () => {},
    };
  }
  return context;
}
